import React, { useEffect, useRef, useState } from 'react';

const ImageCycle = ({ images, captions = [], interval = 3000, size = 'L', caption = null, minHeight = null }) => {
  const foregroundRef = useRef();
  const backgroundRef = useRef();
  const imageIndexRef = useRef(0);
  const isTransitioningRef = useRef(false);
  const intervalRef = useRef();
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    // Check if images array is valid
    if (!images || images.length < 2) {
      return;
    }
    
    // Check if component is still mounted and refs are available
    if (!foregroundRef.current || !backgroundRef.current) {
      return;
    }
    
    // Set initial images
    if (foregroundRef.current) {
      foregroundRef.current.src = images[0];
    }
    if (backgroundRef.current) {
      backgroundRef.current.src = images[1];
    }

    function transitionImage() {
      if (isTransitioningRef.current) return;
      
      isTransitioningRef.current = true;
      imageIndexRef.current = (imageIndexRef.current + 1) % images.length;
      setCurrentIndex(imageIndexRef.current);
      const nextImage = images[imageIndexRef.current];
      
      if (foregroundRef.current && backgroundRef.current) {
        const preloadImage = new window.Image();
        preloadImage.onload = () => {
          if (!backgroundRef.current || !foregroundRef.current) {
            isTransitioningRef.current = false;
            return;
          }

          // Set the background image source to the new image
          backgroundRef.current.src = nextImage;
          
          // Wait a frame to ensure the image is loaded, then fade in background
          requestAnimationFrame(() => {
            // Check if refs are still valid
            if (!backgroundRef.current || !foregroundRef.current) {
              isTransitioningRef.current = false;
              return;
            }
            
            // First, fade in the background image completely
            backgroundRef.current.style.opacity = '1';
            backgroundRef.current.style.transition = 'opacity 0.5s ease-in-out';
            
            setTimeout(() => {
              // Check if refs are still valid
              if (!backgroundRef.current || !foregroundRef.current) {
                isTransitioningRef.current = false;
                return;
              }
              
              // Only after background is fully visible, fade out the foreground
              foregroundRef.current.style.opacity = '0';
              foregroundRef.current.style.transition = 'opacity 0.5s ease-in-out';
              
              setTimeout(() => {
                if (!foregroundRef.current || !backgroundRef.current) {
                  isTransitioningRef.current = false;
                  return;
                }

                // Now swap the images: background becomes foreground
                foregroundRef.current.src = nextImage;
                
                // Reset opacities
                foregroundRef.current.style.opacity = '1';
                foregroundRef.current.style.transition = 'none';
                backgroundRef.current.style.opacity = '0';
                backgroundRef.current.style.transition = 'none';
                
                // Reset transition flag
                isTransitioningRef.current = false;
              }, 500);
            }, 500);
          });
        };

        preloadImage.onerror = () => {
          isTransitioningRef.current = false;
        };
        
        // Start loading the image
        preloadImage.src = nextImage;
      }
    }

    // Start the interval after a short delay
    const startInterval = () => {
      intervalRef.current = setInterval(transitionImage, interval);
    };
    
    setTimeout(startInterval, 1000); // Start after 1 second

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // Reset transition flag on cleanup
      isTransitioningRef.current = false;
    };
  }, [images, interval]);

  return (
    <div className="image-cycle-container" style={{ 
      position: 'relative', 
      width: '100%',
      height: '60vh',
      minHeight: minHeight || (size === 'S' ? '80vh' : '50vh'),
      maxHeight: '80vh',
      overflow: 'hidden',
      borderRadius: 'var(--border-radius)'
    }}>
      {/* Background image layer (for crossfade) */}
      <img
        ref={backgroundRef}
        src={images[1]}
        alt="next"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          zIndex: 1,
          objectFit: 'cover',
          objectPosition: 'top center',
          borderRadius: 'var(--border-radius)'
        }}
      />
      
      {/* Foreground image layer (currently visible) */}
      <img
        ref={foregroundRef}
        src={images[0]}
        alt="current"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 1,
          zIndex: 2,
          objectFit: 'cover',
          objectPosition: 'top center',
          borderRadius: 'var(--border-radius)'
        }}
      />
      
    </div>
  );
};

export default ImageCycle; 